/**
 * Rack Explorer — top half: the site's racks in 3D; bottom left: the selected
 * rack's faces and positions; bottom right: where the selected component (or
 * rack) connects, with a jump to the far end. A query bar drives all three.
 *
 * Presentation only: takes a built RackPlant, owns selection state.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@pazportal/ui';
import {
  CONFIDENCE_LABEL,
  MEDIUM_LABEL,
  checkItems,
  neighbourRacks,
  networkColor,
  rackTitle,
  roomOfRack,
  ruLabel,
  type Confidence,
  type LinkEnd,
  type RackPlant,
  type RackUnitRow,
} from '../../lib/rackModel';
import { QUERY_EXAMPLES, runRackQuery, type QueryHit, type QueryResult } from '../../lib/rackQuery';
import { RackFace } from './RackFace';
import { KIND_LEGEND, kindColor } from '../../lib/deviceColors';
import { RackScene3D } from './RackScene3D';
import { LogicalView } from './LogicalView';
import { NetworkPanel, NetworkPanelPill } from './NetworkPanel';
import { PazNetMark } from '../PazNetMark';

export function RackExplorer({ plant, queryBarSlot = null, onRetireUnit }: {
  plant: RackPlant;
  /** Offered on a selected device's card; the page owns the retire dialog. */
  onRetireUnit?: (unitId: string) => void;
  /** Element in the page header to render the query bar into (top-right). */
  queryBarSlot?: HTMLElement | null;
}) {
  const [rackId, setRackId] = useState<string | null>(plant.racks[0]?.id ?? null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [pathLinkIds, setPathLinkIds] = useState<string[] | undefined>();
  const [overviewNonce, setOverviewNonce] = useState(0);
  const [networkId, setNetworkId] = useState<string | null>(null);
  const [mode, setMode] = useState<'3d' | 'logical'>('3d');
  const [panelTab, setPanelTab] = useState<'networks' | 'racks'>('networks');
  const [panelMinimised, setPanelMinimised] = useState(false);
  const { ref: stageRef, expanded, toggle: toggleExpanded } = useExpandable<HTMLElement>();
  const network = networkId ? plant.networkById.get(networkId) : undefined;

  const selectRack = (id: string) => {
    setRackId(id);
    setUnitId(null);
    setPathLinkIds(undefined);
  };
  const selectUnit = (id: string) => {
    const u = plant.unitById.get(id);
    if (!u) return;
    setRackId(u.rack_id);
    setUnitId(id);
    setPathLinkIds(undefined);
  };
  const selectRoom = (roomId: string) => {
    const first = plant.racksByRoom.get(roomId)?.[0];
    if (first) selectRack(first.id);
  };
  const selectNetwork = (id: string | null) => {
    setNetworkId(id);
    setPathLinkIds(undefined);
    // In full-screen 3D the device labels need the width: tuck the panel into
    // its pill (one click restores it). The logical view keeps the panel.
    if (id && mode === '3d' && expanded) setPanelMinimised(true);
  };

  const onHit = (hit: QueryHit, result: QueryResult) => {
    if (hit.type === 'unit') return selectUnit(hit.id);
    if (hit.type === 'link') return selectUnit(hit.unitId);
    if (hit.type === 'rack') {
      setRackId(hit.id);
      setUnitId(null);
      setPathLinkIds(result.path?.linkIds);
      return;
    }
    selectRoom(hit.id);
  };

  const applyFocus = (result: QueryResult) => {
    if (result.networkId) {
      selectNetwork(result.networkId);
      return;
    }
    if (result.path) {
      setRackId(result.path.racks[result.path.racks.length - 1]);
      setUnitId(null);
      setPathLinkIds(result.path.linkIds);
      return;
    }
    const f = result.focus;
    if (!f) return;
    if (f.type === 'unit') selectUnit(f.id);
    else if (f.type === 'rack') selectRack(f.id);
    else selectRoom(f.id);
  };

  const rackIndex = rackId ? plant.racks.findIndex((r) => r.id === rackId) : -1;
  const step = (dir: 1 | -1) => {
    if (!plant.racks.length) return;
    const next = plant.racks[(rackIndex + dir + plant.racks.length) % plant.racks.length];
    selectRack(next.id);
  };

  const queryBar = <QueryBar plant={plant} contextRackId={rackId} onHit={onHit} onFocus={applyFocus} />;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {!expanded && (queryBarSlot ? createPortal(queryBar, queryBarSlot) : queryBar)}

      <section
        ref={stageRef}
        aria-label="Rack plant view"
        className={cn(
          'relative overflow-hidden border border-border bg-surface',
          expanded ? 'fixed inset-0 z-50 flex flex-col rounded-none border-0 bg-bg' : 'min-h-[320px] flex-[1_1_50%] rounded-lg',
        )}
      >
        {expanded && (
          <div className="relative z-30 flex flex-none items-center justify-between gap-3 border-b border-divider bg-bg-elevated px-3 py-2">
            <span className="truncate text-sm font-bold text-text">Racks</span>
            <div className="w-full max-w-2xl">{queryBar}</div>
          </div>
        )}

        <div className="relative min-h-0 flex-1" style={expanded ? undefined : { height: '100%' }}>
          {mode === '3d' ? (
            <RackScene3D
              plant={plant}
              selectedRackId={rackId}
              selectedUnitId={unitId}
              highlightLinkIds={pathLinkIds}
              networkId={networkId}
              overviewNonce={overviewNonce}
              rightInset={expanded && !panelMinimised ? 344 : 0}
              onSelectRack={selectRack}
              onSelectUnit={selectUnit}
            />
          ) : (
            <LogicalView
              plant={plant}
              networkId={networkId}
              selectedUnitId={unitId}
              onSelectUnit={selectUnit}
              onSelectRoom={selectRoom}
              rightInset={expanded && !panelMinimised ? 344 : 0}
            />
          )}

          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-wrap items-start justify-between gap-2 p-3">
            <div className="flex items-center gap-3">
              <PazNetMark size={44} />
              <Legend mode={mode} />
            </div>
            <div className="pointer-events-auto flex flex-wrap gap-1.5">
              <div role="tablist" aria-label="View" className="flex overflow-hidden rounded-sm border border-border bg-bg-elevated">
                {(['3d', 'logical'] as const).map((m) => (
                  <button
                    key={m}
                    role="tab"
                    aria-selected={mode === m}
                    onClick={() => setMode(m)}
                    className={cn('h-8 px-3 text-sm font-medium', mode === m ? 'bg-primary text-text-inverse' : 'text-text hover:bg-surface-3')}
                  >
                    {m === '3d' ? '3D' : 'Logical'}
                  </button>
                ))}
              </div>
              {mode === '3d' && (
                <>
                  <SceneButton onClick={() => step(-1)} label="Previous rack">‹</SceneButton>
                  <SceneButton onClick={() => step(1)} label="Next rack">›</SceneButton>
                  <SceneButton
                    onClick={() => {
                      setUnitId(null);
                      setPathLinkIds(undefined);
                      setOverviewNonce((n) => n + 1);
                    }}
                    label="Whole site"
                  >
                    Site
                  </SceneButton>
                </>
              )}
              <SceneButton onClick={toggleExpanded} label={expanded ? 'Exit full screen' : 'Full screen'}>
                <ExpandIcon expanded={expanded} />
              </SceneButton>
            </div>
          </div>

          {expanded && (
            <div className="pointer-events-none absolute bottom-3 right-3 top-16 z-20 flex">
              {panelMinimised ? (
                <NetworkPanelPill plant={plant} networkId={networkId} onRestore={() => setPanelMinimised(false)} />
              ) : (
                <NetworkPanel
                  plant={plant}
                  networkId={networkId}
                  selectedRackId={rackId}
                  selectedUnitId={unitId}
                  tab={panelTab}
                  onTab={setPanelTab}
                  onMinimize={() => setPanelMinimised(true)}
                  onSelectNetwork={selectNetwork}
                  onSelectUnit={selectUnit}
                  onSelectRoom={selectRoom}
                  onSelectRack={selectRack}
                />
              )}
            </div>
          )}

          <div className="absolute bottom-3 left-3 z-10 flex flex-col items-start gap-1.5" style={mode === 'logical' ? { bottom: '3.25rem' } : undefined}>
            {network && (
              <div className="flex items-center gap-2 rounded-sm border border-border bg-bg-elevated px-2 py-1 text-xs text-text">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: networkColor(network) }} />
                {network.name}
                <button className="text-primary hover:underline" onClick={() => selectNetwork(null)}>clear</button>
              </div>
            )}
            {pathLinkIds && (
              <div className="rounded-sm border border-border bg-bg-elevated px-2 py-1 text-xs text-text-muted">
                Showing a path · <button className="text-primary hover:underline" onClick={() => setPathLinkIds(undefined)}>clear</button>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="grid min-h-0 flex-[1_1_50%] grid-cols-1 gap-3 lg:grid-cols-2">
        <section className="min-h-0 overflow-auto rounded-lg border border-border bg-surface" aria-label="Rack components">
          {rackId ? (
            <RackDetail plant={plant} rackId={rackId} unitId={unitId} onSelectUnit={selectUnit} onRetireUnit={onRetireUnit} />
          ) : (
            <p className="p-5 text-sm text-text-muted">Select a rack.</p>
          )}
        </section>
        <section className="min-h-0 overflow-auto rounded-lg border border-border bg-surface" aria-label="Connections">
          <Connections plant={plant} rackId={rackId} unitId={unitId} onSelectRack={selectRack} onSelectUnit={selectUnit} />
        </section>
      </div>
    </div>
  );
}

/**
 * Full-screen toggle for one element: the browser Fullscreen API when it is
 * available, a fixed overlay otherwise (and while the API is denied). Esc
 * leaves either way.
 */
function useExpandable<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) setExpanded(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) setExpanded(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const toggle = () => {
    if (expanded) {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
      setExpanded(false);
      return;
    }
    setExpanded(true);
    ref.current?.requestFullscreen?.().catch(() => {
      /* overlay only */
    });
  };
  return { ref, expanded, toggle };
}

function ExpandIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      {expanded ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
      )}
    </svg>
  );
}

function SceneButton({ onClick, label, children }: { onClick: () => void; label: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="h-8 min-w-8 rounded-sm border border-border bg-bg-elevated px-2.5 text-sm font-medium text-text hover:bg-surface-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {children}
    </button>
  );
}

function Legend({ mode }: { mode: '3d' | 'logical' }) {
  if (mode === 'logical') {
    return (
      <div className="pointer-events-auto flex flex-wrap items-center gap-x-3 gap-y-1 rounded-sm border border-border bg-bg-elevated/90 px-2.5 py-1.5 text-[11px] text-text-muted">
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-accent-orange" />Fiber</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-info" />Copper</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-accent-purple" />Logical</span>
        <span className="inline-flex items-center gap-1.5"><svg width="18" height="4" aria-hidden><line x1="0" y1="2" x2="18" y2="2" stroke="currentColor" strokeWidth="2" strokeDasharray="5 3" /></svg>Not verified</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-warning" />Contested</span>
      </div>
    );
  }
  return (
    <div className="pointer-events-auto flex max-w-[min(100%,46rem)] flex-wrap items-center gap-x-3 gap-y-1 rounded-sm border border-border bg-bg-elevated/90 px-2.5 py-1.5 text-[11px] text-text-muted">
      {KIND_LEGEND.map(({ label, kind }) => (
        <span key={kind} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-3.5 rounded-[2px]" style={{ background: kindColor(kind) }} />
          {label}
        </span>
      ))}
      <span className="mx-0.5 h-3 w-px bg-border" aria-hidden />
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-3.5 rounded-[2px] bg-text-muted opacity-50" />Deduced</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-3.5 rounded-[2px] border-[1.5px] border-warning" />Contested / unknown</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-accent-gold" />Check on site</span>
      <span className="text-text-faint">· select a device to show its links</span>
    </div>
  );
}

// ─── Query bar ───────────────────────────────────────────────────────────────

function QueryBar({
  plant, contextRackId, onHit, onFocus,
}: {
  plant: RackPlant;
  contextRackId: string | null;
  onHit: (hit: QueryHit, result: QueryResult) => void;
  onFocus: (result: QueryResult) => void;
}) {
  const [text, setText] = useState('');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const checks = useMemo(() => checkItems(plant).length, [plant]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const run = (q: string) => {
    const r = runRackQuery(plant, q, contextRackId);
    setResult(r);
    setOpen(true);
    onFocus(r);
  };

  const pick = (hit: QueryHit) => {
    if (hit.id.startsWith('example:')) {
      const q = hit.id.slice('example:'.length);
      setText(q);
      run(q);
      return;
    }
    if (result) onHit(hit, result);
  };

  return (
    <div className="relative">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(text);
        }}
        className="flex items-center gap-2"
        role="search"
      >
        <label htmlFor="rack-query" className="sr-only">Ask the rack plant</label>
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-xs text-text-faint">&gt;</span>
          <input
            id="rack-query"
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => result && setOpen(true)}
            onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
            placeholder={`Ask the plant — "${QUERY_EXAMPLES[0]}", "${QUERY_EXAMPLES[3]}"   ( / , help )`}
            className="h-9 w-full rounded-sm border border-border bg-surface pl-7 pr-3 font-mono text-sm text-text placeholder:text-text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            autoComplete="off"
          />
        </div>
        <button type="submit" className="h-9 rounded-sm bg-primary px-4 text-sm font-medium text-text-inverse hover:bg-primary-hover">
          Run
        </button>
        <button
          type="button"
          onClick={() => {
            setText('check');
            run('check');
          }}
          className="h-9 whitespace-nowrap rounded-sm border border-border bg-surface px-3 text-sm text-text hover:bg-surface-3"
        >
          Check on site <span className="ml-1 rounded-full bg-accent-gold-soft px-1.5 text-xs text-text">{checks}</span>
        </button>
      </form>

      {open && result && (
        <div className="absolute left-0 right-0 top-11 z-20 max-h-72 overflow-auto rounded-sm border border-border bg-bg-elevated shadow-lg">
          <div className="flex items-center justify-between border-b border-divider px-3 py-2">
            <span className="font-mono text-xs text-text-muted">{result.intent}</span>
            <button className="text-xs text-text-muted hover:text-text" onClick={() => setOpen(false)}>close</button>
          </div>
          {result.hits.length === 0 ? (
            <p className="px-3 py-3 text-sm text-text-muted">Nothing recorded matches. Type "help" for what you can ask.</p>
          ) : (
            <ul>
              {result.hits.map((hit) => (
                <li key={`${hit.type}:${hit.id}`}>
                  <button
                    type="button"
                    onClick={() => pick(hit)}
                    className="flex w-full items-baseline gap-3 px-3 py-1.5 text-left hover:bg-surface-3 focus:bg-surface-3 focus:outline-none"
                  >
                    <span className="w-10 flex-none font-mono text-[10px] uppercase text-text-faint">{hit.id.startsWith('example:') ? 'try' : hit.type}</span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-text">{hit.title}</span>
                      {hit.sub && <span className="block truncate text-xs text-text-muted">{hit.sub}</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Bottom left: the rack and its components ───────────────────────────────

function RackDetail({ plant, rackId, unitId, onSelectUnit, onRetireUnit }: {
  plant: RackPlant; rackId: string; unitId: string | null; onSelectUnit: (id: string) => void;
  onRetireUnit?: (unitId: string) => void;
}) {
  const rack = plant.rackById.get(rackId)!;
  const room = roomOfRack(plant, rackId);
  const units = plant.unitsByRack.get(rackId) ?? [];
  const hasRear = units.some((u) => u.face === 'rear');
  const selected = unitId ? plant.unitById.get(unitId) : undefined;
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const detailRef = useRef<HTMLTableRowElement>(null);
  // The status of the selected line opens directly beneath it; clicking the
  // line again folds it away. A new selection always opens.
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    setCollapsed(false);
    if (!unitId) return;
    // Bring the line and the status under it into view together.
    requestAnimationFrame(() => {
      rowRefs.current.get(unitId)?.scrollIntoView({ block: 'nearest' });
      detailRef.current?.scrollIntoView({ block: 'nearest' });
    });
  }, [unitId]);

  return (
    <div className="space-y-4 p-4">
      <header className="space-y-1 border-t-2 border-text pt-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-bold text-text">
            {rackTitle(plant, rackId)} <span className="ml-1 font-mono text-xs font-normal text-text-muted">{rack.height_u}U{rack.height_captured ? '' : ' (height not captured)'} · {rack.numbering === 'bottom_up' ? 'U1 at bottom' : 'numbered from top'}</span>
          </h2>
        </div>
        {room && (
          <dl className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
            {room.data_as_of && <Field label="Data as of" value={room.data_as_of} />}
            {room.source && <Field label="Source" value={room.source} />}
          </dl>
        )}
        {(room?.note || rack.note) && <p className="max-w-prose text-xs text-text-muted">{rack.note ?? room?.note}</p>}
      </header>

      <div className="flex gap-3 overflow-x-auto pb-1">
        <RackFace rack={rack} units={units} face="front" selectedUnitId={unitId} onSelect={onSelectUnit} />
        {hasRear ? (
          <RackFace rack={rack} units={units} face="rear" selectedUnitId={unitId} onSelect={onSelectUnit} />
        ) : (
          <p className="w-40 flex-none self-center text-xs text-text-faint">Rear face not surveyed.</p>
        )}
      </div>

      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="bg-surface-3 text-left font-mono text-[10.5px] uppercase tracking-wider text-text-muted">
            <th className="px-2 py-1.5 font-medium">RU</th>
            <th className="px-2 py-1.5 font-medium">Device</th>
            <th className="px-2 py-1.5 font-medium">State</th>
          </tr>
        </thead>
        <tbody>
          {units.map((u) => {
            const links = plant.linksByUnit.get(u.id)?.length ?? 0;
            const open = u.id === unitId && !collapsed;
            return (
              <Fragment key={u.id}>
              <tr
                ref={(el) => { if (el) rowRefs.current.set(u.id, el); else rowRefs.current.delete(u.id); }}
                onClick={() => {
                  if (u.kind === 'vacant') return;
                  if (u.id === unitId) setCollapsed((c) => !c);
                  else onSelectUnit(u.id);
                }}
                aria-expanded={u.kind === 'vacant' ? undefined : open}
                className={cn(
                  'border-b border-divider',
                  u.kind !== 'vacant' && 'cursor-pointer hover:bg-surface-3',
                  u.id === unitId && 'bg-primary-soft',
                  open && 'border-b-0',
                )}
              >
                <td className="whitespace-nowrap px-2 py-1 font-mono text-text-muted">{ruLabel(u)}</td>
                <td className="px-2 py-1">
                  <span className={cn(u.kind === 'vacant' ? 'text-text-faint' : 'text-text')}>{u.label}</span>
                  {u.detail && <span className="block text-xs text-text-muted">{u.detail}</span>}
                  {links > 0 && <span className="mt-0.5 inline-block font-mono text-[10.5px] text-primary">{links} link{links === 1 ? '' : 's'}</span>}
                </td>
                <td className="whitespace-nowrap px-2 py-1">
                  <ConfidenceChip c={u.confidence} />
                  {u.check_note && <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-accent-gold align-middle" title={u.check_note} />}
                </td>
              </tr>
              {open && selected && (
                <tr ref={detailRef} className="border-b border-divider bg-primary-soft">
                  <td colSpan={3} className="px-2 pb-2 pt-0">
                    <UnitCard unit={selected} onRetire={onRetireUnit ? () => onRetireUnit(selected.id) : undefined} />
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UnitCard({ unit, onRetire }: { unit: RackUnitRow; onRetire?: () => void }) {
  return (
    <div className="rounded-sm border border-primary bg-bg-elevated p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold text-text">{unit.label}</h3>
        <ConfidenceChip c={unit.confidence} />
      </div>
      {unit.detail && <p className="mt-0.5 text-sm text-text-muted">{unit.detail}</p>}
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-4">
        <Field label="Position" value={ruLabel(unit)} />
        <Field label="Height" value={`${unit.ru_height}U`} />
        <Field label="Kind" value={unit.kind.replace('_', ' ')} />
        <Field label="Owner" value={unit.owner ?? '—'} />
        {unit.ip_address && <Field label="IP" value={unit.ip_address} />}
        {unit.mac_address && <Field label="MAC" value={unit.mac_address.toLowerCase()} />}
        <Field label="Asset" value={unit.asset_id ? 'linked' : 'not inventoried'} />
      </dl>
      {unit.check_note && (
        <p className="mt-2 flex items-start gap-2 rounded-sm bg-accent-gold-soft px-2 py-1.5 text-xs text-text">
          <span className="mt-1 h-2 w-2 flex-none rounded-full bg-accent-gold" />
          <span><span className="font-semibold">Check on site:</span> {unit.check_note}</span>
        </p>
      )}
      {onRetire && unit.kind !== 'vacant' && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRetire(); }}
            className="h-7 rounded-sm border border-border px-2.5 text-xs text-text-muted hover:border-error hover:text-error"
          >
            Retire device…
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className="m-0 truncate font-mono text-text">{value}</dd>
    </div>
  );
}

function ConfidenceChip({ c }: { c: Confidence }) {
  const cls: Record<Confidence, string> = {
    verified: 'bg-success-soft text-success',
    deduced: 'bg-surface-3 text-success',
    contested: 'bg-warning-soft text-warning',
    unknown: 'bg-warning-soft text-warning',
    vacant: 'bg-surface-3 text-text-muted',
  };
  return <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium', cls[c])}>{CONFIDENCE_LABEL[c]}</span>;
}

// ─── Bottom right: where it connects ────────────────────────────────────────

function Connections({ plant, rackId, unitId, onSelectRack, onSelectUnit }: {
  plant: RackPlant; rackId: string | null; unitId: string | null;
  onSelectRack: (id: string) => void; onSelectUnit: (id: string) => void;
}) {
  if (!rackId) return <p className="p-5 text-sm text-text-muted">Select a rack or component to see where it connects.</p>;
  const unit = unitId ? plant.unitById.get(unitId) : undefined;
  const ends = unit ? plant.linksByUnit.get(unit.id) ?? [] : plant.linksByRack.get(rackId) ?? [];
  const neighbours = neighbourRacks(plant, rackId);

  const go = (end: LinkEnd) => (end.otherUnitId ? onSelectUnit(end.otherUnitId) : onSelectRack(end.otherRackId));

  return (
    <div className="space-y-4 p-4">
      <header className="border-t-2 border-text pt-2">
        <h2 className="text-lg font-bold text-text">Connections</h2>
        <p className="text-xs text-text-muted">
          {unit ? <>From <span className="font-medium text-text">{unit.label}</span> · {ruLabel(unit)}</> : <>Everything recorded for {rackTitle(plant, rackId)}</>}
        </p>
      </header>

      {!unit && neighbours.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-[10.5px] font-medium uppercase tracking-wider text-text-muted">Next racks</h3>
          <div className="flex flex-wrap gap-2">
            {neighbours.map((n) => (
              <button
                key={n.rackId}
                onClick={() => onSelectRack(n.rackId)}
                className="inline-flex items-center gap-2 rounded-sm border border-border bg-bg-elevated px-3 py-1.5 text-sm text-text hover:border-primary"
              >
                {rackTitle(plant, n.rackId)}
                <span className="font-mono text-xs text-text-muted">{n.count}</span>
                <span aria-hidden className="text-primary">→</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {ends.length === 0 ? (
        <p className="text-sm text-text-muted">
          {unit ? 'No connection recorded for this component.' : 'No connection recorded for this rack.'} Links appear here once the survey names where a cable lands.
        </p>
      ) : (
        <ul className="space-y-2">
          {ends.map((end) => {
            const near = plant.unitById.get(end.link.from_unit_id === end.otherUnitId ? end.link.to_unit_id ?? '' : end.link.from_unit_id);
            const far = end.otherUnitId ? plant.unitById.get(end.otherUnitId) : undefined;
            const sameRack = end.otherRackId === rackId;
            return (
              <li key={end.link.id + (end.otherUnitId ?? '')} className="rounded-sm border border-border bg-bg-elevated p-3">
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  <MediumChip medium={end.link.medium} />
                  <ConfidenceChip c={end.link.confidence} />
                  {end.link.label && <span className="font-medium text-text">{end.link.label}</span>}
                </div>
                <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-sm">
                  <span className="min-w-0 truncate text-text-muted">{!unit && near ? `${ruLabel(near)} ${near.label}` : unit ? unit.label : ''}</span>
                  <span aria-hidden className="text-text-faint">⟶</span>
                  <span className="min-w-0 truncate text-text">
                    {far ? `${far.label}` : 'rack (device not traced)'}
                    <span className="block truncate text-xs text-text-muted">{rackTitle(plant, end.otherRackId)}{far ? ` · ${ruLabel(far)}` : ''}</span>
                  </span>
                </div>
                {end.link.note && <p className="mt-2 text-xs text-text-muted">{end.link.note}</p>}
                <div className="mt-2 flex gap-2">
                  <button onClick={() => go(end)} className="rounded-sm bg-primary px-3 py-1 text-xs font-medium text-text-inverse hover:bg-primary-hover">
                    {sameRack ? 'Select' : `Go to ${plant.rackById.get(end.otherRackId)?.code}`} →
                  </button>
                  {!unit && near && (
                    <button onClick={() => onSelectUnit(near.id)} className="rounded-sm border border-border px-3 py-1 text-xs text-text hover:bg-surface-3">
                      Select {ruLabel(near)}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function MediumChip({ medium }: { medium: keyof typeof MEDIUM_LABEL }) {
  const cls = medium.startsWith('fiber') ? 'bg-accent-orange-soft text-accent-orange'
    : medium === 'copper' ? 'bg-info-soft text-info'
    : 'bg-accent-purple-soft text-accent-purple';
  return <span className={cn('rounded-full px-2 py-0.5 font-medium', cls)}>{MEDIUM_LABEL[medium]}</span>;
}
